/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Date text inputs for the DateRangeInput
 */

import { useTheme } from '@emotion/react'
import { addDays, isBefore } from 'date-fns'
import React, { useEffect, useState } from 'react'

import { MaskedInput } from '../../MaskedInput'
import { getInputDateFormat } from '../helpers/getInputDateFormat'
import * as style from '../style'
import { Errors, UndefinedOrDate } from '../types'
export interface CalendarDateInputsProps {
  endDate: UndefinedOrDate
  endInputPlaceholder: string
  errors: Errors[]
  minimumStay?: number
  setEndDate: (date: UndefinedOrDate) => void
  setStartDate: (date: UndefinedOrDate) => void
  startDate: UndefinedOrDate
  startInputPlaceholder: string
}

export const CalendarDateInputs = ({
  endDate,
  endInputPlaceholder,
  errors,
  minimumStay,
  setEndDate,
  setStartDate,
  startDate,
  startInputPlaceholder,
}: CalendarDateInputsProps) => {
  const [startDateValue, setStartDateValue] = useState('')
  const [endDateValue, setEndDateValue] = useState('')
  const hasError = errors.length !== 0
  const isErrorEnd = errors.filter((er) => er.type === 'end')
  const isErrorStart = errors.filter((er) => er.type === 'start')
  const { colors } = useTheme()

  useEffect(() => {
    setStartDateValue(getInputDateFormat(startDate) ?? '')
  }, [startDate])

  useEffect(() => {
    const endDateVal = getInputDateFormat(endDate) ?? ''

    if (minimumStay && !!startDate && !!endDate) {
      const nextAvailableDay = addDays(startDate, minimumStay)
      const isBeforeMinimumStay = isBefore(endDate, nextAvailableDay)
      setEndDateValue(!isBeforeMinimumStay ? endDateVal : '')
    } else {
      setEndDateValue(endDateVal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate])

  return (
    <div css={style.calendarDateInputsContainerOuter}>
      <div css={style.calendarDateInputsContainer}>
        <MaskedInput
          aria-label="Start Date"
          hideContainerCss={true}
          mask="99/99/9999"
          onBlur={(event) => {
            const parsedDate = Date.parse(event?.target?.value)

            if (Number.isNaN(parsedDate)) {
              setStartDate(undefined)
              setEndDate(undefined)
              return
            }

            setStartDate(new Date(event.target.value))
          }}
          onChange={(value) => setStartDateValue(value)}
          placeholder={startInputPlaceholder}
          style={{
            ...(isErrorEnd.length && { borderColor: colors.errorColor }),
          }}
          type="tel"
          value={startDateValue}
          width="100%"
        />
        <span css={style.middleInputDash}>—</span>
        <MaskedInput
          aria-label="End Date"
          disabled={!startDateValue}
          hideContainerCss={true}
          mask="99/99/9999"
          onBlur={(event) => {
            const parsedDate = Date.parse(event?.target?.value)
            if (Number.isNaN(parsedDate)) {
              setEndDate(undefined)
              return
            }

            setEndDate(new Date(event.target.value))
          }}
          onChange={(value) => setEndDateValue(value)}
          placeholder={endInputPlaceholder}
          style={{
            ...(isErrorEnd.length && { borderColor: colors.errorColor }),
          }}
          type="tel"
          value={endDateValue}
          width="100%"
        />
      </div>
      {hasError && (
        <div css={style.errorContainer}>
          <div>
            {[...isErrorStart, ...isErrorEnd].map(({ message }) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
